// useRoadCases — the hand-written per-domain hooks over the generated client (ART-253, legacy parity). Road cases
// are band-scoped: the list key is roadCasesKey(bandId) = ['band', bandId, 'road-cases'] — the EXACT key the
// invalidation registry invalidates on `road-cases-changed` — and a case's sheet reads a CHILD of it
// (['band', bandId, 'road-cases', caseId]), so the one live signal repaints both. Every mutation surfaces the
// server error verbatim and never reports success on an unpersisted write; nothing here is optimistic — a case
// scanned at the truck must show the state the SERVER holds, or the checklist lies about what is loaded.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError, errorMessage } from "./apiError.js";
import { roadCasesKey } from "../realtime/invalidationRegistry.js";
import { pushToast } from "../toasts.js";

type RoadCaseList = components["schemas"]["RoadCaseList"];
type RoadCaseRow = components["schemas"]["RoadCaseRow"];
type RoadCaseDetail = components["schemas"]["RoadCaseDetail"];
type RoadCaseItemDto = components["schemas"]["RoadCaseItemDto"];
type PairCaseResult = components["schemas"]["PairCaseResult"];
type PublicScanResult = components["schemas"]["PublicScanResult"];

export type { RoadCaseRow, RoadCaseDetail, RoadCaseItemDto, PairCaseResult, PublicScanResult };

/** GET the band's road cases. Empty list = the born-blank state.*/
export function useRoadCases() {
  const { api } = useApp();
  return useBandQuery<RoadCaseList>(
    ["road-cases"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/road-cases", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { staleTime: 15_000 },
  );
}

/** GET one case for its sheet — the row, its items and its sticker pairing. Disabled with no case.*/
export function useRoadCase(caseId: string | null) {
  const { api } = useApp();
  return useBandQuery<RoadCaseDetail>(
    ["road-cases", caseId ?? "none"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/road-cases/{caseId}", {
        params: { path: { bandId, caseId: caseId! } },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    { enabled: !!caseId, staleTime: 15_000 },
  );
}

/** The shared mutation plumbing: refetch the list (and every case sheet under it) on success; toast on failure. */
function useRoadCaseMutation<TData, TVars>(
  fn: (bandId: string, vars: TVars) => Promise<TData>,
  failure: string,
) {
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<TData, TVars>(fn, {
    onSuccess: () => void qc.invalidateQueries({ queryKey: roadCasesKey(bandId) }),
    onError: (err) => pushToast(errorMessage(err, failure)),
  });
}

/** Create a road case (lands at home).*/
export function useCreateCase() {
  const { api } = useApp();
  return useRoadCaseMutation<RoadCaseRow, string>(async (band, name) => {
    const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/road-cases", {
      params: { path: { bandId: band } },
      body: { name },
    });
    if (!response.ok || !data?.data) throw new ApiError(response.status, error);
    return data.data;
  }, "Couldn't add case");
}

/** Rename a case.*/
export function useRenameCase() {
  const { api } = useApp();
  return useRoadCaseMutation<RoadCaseRow, { caseId: string; name: string }>(async (band, { caseId, name }) => {
    const { data, error, response } = await api.PATCH("/api/v1/bands/{bandId}/road-cases/{caseId}", {
      params: { path: { bandId: band, caseId } },
      body: { name },
    });
    if (!response.ok || !data?.data) throw new ApiError(response.status, error);
    return data.data;
  }, "Couldn't rename case");
}

/** Delete a case — its sticker pairing goes with it.*/
export function useDeleteCase() {
  const { api } = useApp();
  return useRoadCaseMutation<void, string>(async (band, caseId) => {
    const { error, response } = await api.DELETE("/api/v1/bands/{bandId}/road-cases/{caseId}", {
      params: { path: { bandId: band, caseId } },
    });
    if (!response.ok) throw new ApiError(response.status, error);
  }, "Couldn't delete case");
}

/** Set a case's state — the row pill, and the /scan arrival.*/
export function useSetCaseState() {
  const { api } = useApp();
  return useRoadCaseMutation<RoadCaseRow, { caseId: string; state: string }>(async (band, { caseId, state }) => {
    const { data, error, response } = await api.PUT("/api/v1/bands/{bandId}/road-cases/{caseId}/state", {
      params: { path: { bandId: band, caseId } },
      body: { state },
    });
    if (!response.ok || !data?.data) throw new ApiError(response.status, error);
    return data.data;
  }, "Couldn't update case");
}

/** Send every case back home.*/
export function useResetCases() {
  const { api } = useApp();
  return useRoadCaseMutation<void, void>(async (band) => {
    const { error, response } = await api.POST("/api/v1/bands/{bandId}/road-cases/reset", {
      params: { path: { bandId: band } },
    });
    if (!response.ok) throw new ApiError(response.status, error);
  }, "Couldn't reset cases");
}

/** Add a packed item to a case.*/
export function useAddCaseItem() {
  const { api } = useApp();
  return useRoadCaseMutation<RoadCaseItemDto, { caseId: string; name: string }>(async (band, { caseId, name }) => {
    const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/road-cases/{caseId}/items", {
      params: { path: { bandId: band, caseId } },
      body: { name },
    });
    if (!response.ok || !data?.data) throw new ApiError(response.status, error);
    return data.data;
  }, "Couldn't add item");
}

/** Remove a packed item.*/
export function useDeleteCaseItem() {
  const { api } = useApp();
  return useRoadCaseMutation<void, { caseId: string; itemId: string }>(async (band, { caseId, itemId }) => {
    const { error, response } = await api.DELETE("/api/v1/bands/{bandId}/road-cases/{caseId}/items/{itemId}", {
      params: { path: { bandId: band, caseId, itemId } },
    });
    if (!response.ok) throw new ApiError(response.status, error);
  }, "Couldn't remove item");
}

/** Pair an NFC sticker to a case — mints (or returns the existing) token + relative /scan?token= path.*/
export function usePairCase() {
  const { api } = useApp();
  return useRoadCaseMutation<PairCaseResult, string>(async (band, caseId) => {
    const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/road-cases/nfc/pair", {
      params: { path: { bandId: band } },
      body: { caseId },
    });
    if (!response.ok || !data?.data) throw new ApiError(response.status, error);
    return data.data;
  }, "Couldn't pair case");
}

/** Unpair a sticker by caseId — the printed token stops resolving immediately.*/
export function useUnpairCase() {
  const { api } = useApp();
  return useRoadCaseMutation<void, string>(async (band, caseId) => {
    const { error, response } = await api.POST("/api/v1/bands/{bandId}/road-cases/nfc/unpair", {
      params: { path: { bandId: band } },
      body: { caseId },
    });
    if (!response.ok) throw new ApiError(response.status, error);
  }, "Couldn't unpair case");
}

/** Stamp that a case's token was written onto a physical sticker (after a successful in-app write).*/
export function useMarkWritten() {
  const { api } = useApp();
  return useRoadCaseMutation<void, string>(async (band, caseId) => {
    const { error, response } = await api.POST("/api/v1/bands/{bandId}/road-cases/nfc/written", {
      params: { path: { bandId: band } },
      body: { caseId },
    });
    if (!response.ok) throw new ApiError(response.status, error);
  }, "Couldn't mark the sticker written");
}

/**
 * SET a scanned sticker's case to the scan-mode target. A set, never a toggle — re-scanning leaves the case where
 * it is (the legacy rewrite ruling). No toast here: scan mode reports each tap itself (a sticker of another band
 * reads "not paired to a case in this band", a tap-by-tap message rather than a generic failure).
 */
export function useScanCase() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<RoadCaseRow, { token: string; state: string }>(
    async (band, { token, state }) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/road-cases/nfc/scan", {
        params: { path: { bandId: band } },
        body: { token, state },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    { onSuccess: () => void qc.invalidateQueries({ queryKey: roadCasesKey(bandId) }) },
  );
}

/**
 * Resolve a scanned sticker token → its road case via the PUBLIC `/scan` endpoint (no band in the path — the token
 * is the credential). Read-only display, plus the sticker's OWN band so the `/scan` arrival can act under it. A 404
 * (unknown/unpaired token or a removed case) surfaces as the query's error state (the "not paired" branch).
 */
export function usePublicScan(token: string | undefined) {
  const { api } = useApp();
  return useQuery<PublicScanResult>({
    queryKey: ["public-scan", token],
    enabled: !!token,
    retry: false,
    queryFn: async () => {
      const { data, error, response } = await api.POST("/api/v1/public/scan", { body: { token: token! } });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
  });
}
